import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { NavLink } from 'react-router-dom';

import colors from '#src/colors';
import {
  color, font, motion, radius, shadow,
} from '#src/design/tokens';

// Playful button. Variants: primary (yellow, the one main action), secondary (white), ghost (no
// border); sizes sm / md / lg (md and lg are >= 44px tall); `loading` shows dots and blocks clicks.
// The offset shadow is the button's "depth": hover lifts it a little, press pushes it flat.
const SIZES = {
  sm: { minHeight: 36, font: 14, pad: '6px 14px' },
  md: { minHeight: 44, font: 16, pad: '10px 18px' },
  lg: { minHeight: 56, font: 18, pad: '14px 28px' },
};

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: .5; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const baseButtonStyle = css<ButtonProps>`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  font-family: ${font.family};
  font-weight: 600;
  font-size: ${props => SIZES[props.size || 'md'].font}px;
  line-height: 1.2;
  min-height: ${props => SIZES[props.size || 'md'].minHeight}px;
  padding: ${props => SIZES[props.size || 'md'].pad};
  cursor: pointer;
  border-radius: ${radius.md}px;
  user-select: none;
  border: 0;
  position: relative;
  transition: transform ${motion.fast} ${motion.ease}, box-shadow ${motion.fast} ${motion.ease}, background-color ${motion.fast} ${motion.ease};
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &[aria-busy="true"] { cursor: progress; }
`;

const primaryButtonStyle = css<ButtonProps>`
  ${baseButtonStyle}
  background-color: ${props => props.backgroundColor || (props.variant === 'secondary' ? color.surface : color.primary)};
  color: ${props => props.color || color.ink};
  border: ${props => props.noBorder ? '0px' : `2px solid ${color.border}`};
  box-shadow: ${props => props.noShadow ? 'none' : shadow.sm};
  margin-bottom: ${props => props.noBorder ? '0px' : '3px'};
  transform: translate(0px, 0px);
  &:hover:not(:disabled) {
    background-color: ${props => props.backgroundColor || (props.variant === 'secondary' ? color.primarySoft : color.primaryHover)};
  }
  @media (prefers-reduced-motion: no-preference) {
    &:hover:not(:disabled) {
      transform: ${props => props.noShadow ? 'none' : 'translate(-1px, -1px)'};
      box-shadow: ${props => props.noShadow ? 'none' : `4px 4px 0 0 ${color.border}`};
    }
  }
  &:active:not(:disabled) {
    transform: ${props => props.noShadow ? 'none' : motion.press};
    box-shadow: ${shadow.pressed};
  }
  &:disabled:not([aria-busy="true"]) {
    cursor: default;
    transform: none;
    box-shadow: ${props => props.noShadow ? 'none' : shadow.sm};
    background-color: ${props => props.backgroundColor || colors.lightGrey};
    color: ${color.inkMuted};
  }
`;

const ghostButtonStyle = css<ButtonProps>`
  ${baseButtonStyle}
  background-color: transparent;
  color: ${props => props.color || color.ink};
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover:not(:disabled) { background-color: ${color.primarySoft}; }
  &:disabled { opacity: .5; cursor: default; }
`;

const styleFor = css<ButtonProps>`
  ${props => (props.variant === 'ghost' ? ghostButtonStyle : primaryButtonStyle)}
`;

const PrimaryStyledButton = styled.button<ButtonProps>`
  ${styleFor}
`;

const HrefStyledButton = styled.a.attrs(() => ({}))<any>`
  ${styleFor}
`;

const ToStyledButton = styled(NavLink as React.ComponentType<any>)`
  ${styleFor}
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 4px;
  & > span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${bounce} 900ms infinite ease-in-out;
  }
  & > span:nth-child(2) { animation-delay: 120ms; }
  & > span:nth-child(3) { animation-delay: 240ms; }
`;

export const LoadingDots = () => (
  <Dots aria-hidden="true"><span /><span /><span /></Dots>
);

interface ButtonProps {
  children?: React.ReactNode;
  className?: string;
  href?: string;
  to?: string;
  backgroundColor?: string;
  color?: string;
  noShadow?: boolean;
  noBorder?: boolean;
  variant?: string;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  [key: string]: any;
}

// Style-only props stay off the DOM ($-prefixed for styled-components).
const transient = ({
  backgroundColor, color: textColor, noShadow, noBorder, size, ...rest
}: ButtonProps) => ({
  styleProps: {
    backgroundColor, color: textColor, noShadow, noBorder, size,
  },
  rest,
});

export const ButtonV2 = React.forwardRef<any, ButtonProps>(({
  className,
  to,
  href,
  children,
  variant,
  onClick,
  loading,
  disabled,
  ...props
}, ref) => {
  const { styleProps, rest } = transient(props);
  const content = loading ? (<><LoadingDots />{children}</>) : children;
  const shared = {
    className, variant, ...styleProps, ...rest, ref,
  };
  if (to) {
    return (
      <ToStyledButton type="button" to={to} onClick={onClick} {...shared}>
        {content}
      </ToStyledButton>
    );
  }
  if (href) {
    return (
      <HrefStyledButton type="button" href={href} onClick={onClick} {...shared}>
        {content}
      </HrefStyledButton>
    );
  }
  return (
    <PrimaryStyledButton
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...shared}
    >
      {content}
    </PrimaryStyledButton>
  );
});
